import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { IconAlertSquareRoundedFilled, IconList, IconMap } from "@tabler/icons-react";
import { classNames, useAppConfig, useIsMounted } from "@ct-react/core";
import { useLocaleFormatter } from "@ct-react/locale";
import { ArticleType } from "@shared/url";
import { SearchResult } from "@shared/graphql";
import { actions, articleTypes, routes } from "../i18n/sharable-defs";
import { useGQLSearchFormatter } from "../hooks/format-gql-variables";
import { useMediaQueryDown } from "../hooks/media-queries";
import { PaginationPayload } from "../models/search";
import * as criteriaTools from "../tools/search-params/search-criteria";
import * as paginationTools from "../tools/search-params/search-pagination";
import { AnyArticleSearch, useSearchContext } from "../contexts/search";
import { Wrapper } from "../components/layout/wrapper";
import ArticleCard from "../components/article-card";
import { MapAsyncPopupMarker, MapContainer } from "../bundles/map";
import Switch from "../components/common/switch";
import Button from "../components/common/button";
import InfoBox from "../components/common/info-box";
import SearchedMoreFilter from "../components/search/more-filters";
import SearchedPagination from "../components/search/pagination";
import { useWishlist } from "../contexts/wishlist";
import "./search.scss";
import SeoMetas from "../components/layout/seo-metas";

const transDefs = defineMessages({
  result: { id: "search.results.result", defaultMessage: "{count, plural, =1 {# Résultat} other {# Résultats}}" },
  empty: { id: "search.results.empty", defaultMessage: "Aucun résultat" },
  emptyDetail: { id: "search.results.empty-detail", defaultMessage: "Aucun bien ne correspond à votre recherche. Ajustez vos critères ou modifiez certains de vos filtres." },
});

const ANY_SEARCH_GQL_DATA = gql`
  query search($where: AnyArticleFilterInput, $sort: AnyArticleSortInput, $first: Int, $last: Int, $before: String, $after: String) {
    search(where: $where, sort: $sort, first: $first, last: $last, before: $before, after: $after) {
      totalCount
      edges {
        cursor
        node {
          ... on RentalAccommodationArticle {
            id slug
            novelty recommended promoted
            images @limit(max: 10) { assets { url breakpoint } }
            address { city street }
            title { value }
            lowestPrice { type onDemand value }
            features { bedrooms bathrooms maxGuests}
          }
          ... on SeasonalAccommodationArticle {
            id slug
            novelty recommended promoted
            images @limit(max: 10) { assets { url breakpoint } }
            address { city street }
            title { value }
            seasonalPrice: price { onDemand value isMonthly }
            features { bedrooms bathrooms maxGuests}
          }
          ... on AnnualAccommodationArticle {
            id slug
            novelty recommended promoted
            images @limit(max: 10) { assets { url breakpoint } }
            address { city street }
            title { value }
            annualPrice: price { onDemand value }
            features { bedrooms bathrooms}
          }
          ... on SaleAccommodationArticle {
            id slug
            novelty recommended promoted sold reserved
            images @limit(max: 10) { assets { url breakpoint } }
            address { city street }
            title { value }
            salePrice: price { onDemand value }
            features { bedrooms bathrooms}
          }
        }
        accommodationBookingSuggestion {
          period
          priceOnDemand price { amount }
        }
      }
      accommodationMapMarkers { coordinates id slug }
      paginationInfo {
        currentPage pageCount pageSize hasNextPage hasPreviousPage
        lastPageCursor nextPageCursor previousPageCursor linkPageCursors { cursor page }
      }
    }
  }
`;

const normalizeArticlePrice = (node: any) => {
  if (!node) return node;
  const { seasonalPrice, annualPrice, salePrice, ...article } = node;
  return {
    ...article,
    price: seasonalPrice ?? annualPrice ?? salePrice
  };
};

const SearchPage = ({ type }: { type: ArticleType }) => {

  const intl = useIntl();
  const { options: { mapToken } } = useAppConfig();
  const isMounted = useIsMounted();
  const { transLib } = useLocaleFormatter();
  const navigate = useNavigate();

  const pageSize = useRef<number>(12);
  const mapRef = useRef<HTMLElement>(null);

  const [ searchParams, setSearchParams ] = useSearchParams();
  const [ ctx, dispatch ] = useSearchContext();
  const { popup } = useWishlist();

  const [ withMap, setWithMap ] = useState<boolean>(false);
  const { where, ...others } = useGQLSearchFormatter(pageSize.current, type, searchParams);

  const formattedQuerySort = useMemo(() => {
    if (!type) return undefined;
    switch (type) {
      case ArticleType.RENTAL: return { rentalAccommodation: {price: "ASC"} };
      case ArticleType.SEASONAL: return { seasonalAccommodation: {price: "ASC"} };
      case ArticleType.ANNUAL: return { annualAccommodation: {price: "ASC"} };
      case ArticleType.SALE: return { saleAccommodation: {price: "ASC"} };
    }
  }, [ type ]);

  const { data, loading } = useQuery<{ search: SearchResult }>(ANY_SEARCH_GQL_DATA, {
    ssr: false,
    variables: {
      where,
      ...others,
      sort: formattedQuerySort,
     }
  });

  const isDisplayUntilTablet = useMediaQueryDown("tablet");

  useEffect(() => {
    if (searchParams.size > 0 || Object.keys(ctx[type].saving).length === 0) return;
    navigate(`?${criteriaTools.rebuild(ctx[type].saving).toString()}`, { replace: true });
  }, []);

  useEffect(() => {
    if (!isMounted || !withMap || !isDisplayUntilTablet) return;
    mapRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [ withMap ]);

  const onPaginate = (value: PaginationPayload) => {
    paginationTools.rebuild(value, searchParams);
    setSearchParams(searchParams);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const onFilter = (payload: AnyArticleSearch) => {
    const rebuild = new URLSearchParams();
    criteriaTools.rebuild(payload, rebuild);
    setSearchParams(rebuild);
    dispatch({ type, payload });
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }

  const wrapperClasses= classNames("search-wrapper");
  const resultClasses = classNames("search-results", { "with-map": withMap });
  const listClasses = classNames("search-listed", { empty: !loading && data?.search.totalCount === 0 })

  return (
    <Wrapper.Centered className={wrapperClasses}>
      <SeoMetas title={intl.formatMessage(articleTypes[type])} />
      {popup()}
      <div className="search-more-filters">

        <h1>{intl.formatMessage(articleTypes[type])}</h1>

        <div className="filters">
          <SearchedMoreFilter criteria={criteriaTools.parse(searchParams, type)}
                              onCriteriaChange={onFilter} />
          <span>{intl.formatMessage(transDefs.result,{count: data?.search.totalCount || 0 })}</span>
        </div>

        <div className="map-toggler">
          {intl.formatMessage(actions.showMap)}
          <Switch active={withMap} onChange={setWithMap} />
        </div>

        <Button className="primary with-icon mobile-map-toggler"
                onClick={() => setWithMap(prev => !prev)}>
          {intl.formatMessage(withMap ? actions.showList : actions.showMap)}
          {withMap ? <IconMap /> : <IconList />}
        </Button>

      </div>

      <div className={resultClasses}>
        <main>
          <div className={listClasses}>
            {(data?.search.edges || Array.from({ length: pageSize.current })).map((edge: any, i: number) => (
              <ArticleCard key={edge?.node.id || i} loading={loading} data={{
                ...normalizeArticlePrice(edge?.node),
                ...(!!edge?.accommodationBookingSuggestion) && { suggestion: edge.accommodationBookingSuggestion }
              }} />))
            }
            {data?.search.totalCount === 0 &&
              <InfoBox icon={<IconAlertSquareRoundedFilled />}
                       msg={intl.formatMessage(transDefs.empty)}>
                {intl.formatMessage(transDefs.emptyDetail)}
              </InfoBox>
            }
          </div>
          {!!data?.search &&
            <SearchedPagination data={data.search.paginationInfo}
                                onPaginate={onPaginate} />
          }
        </main>
        <aside ref={mapRef}
               className="search-map">
          <MapContainer mapboxToken={mapToken}
                        className="map-wrapper"
                        center={[ 46.10859470576092, 7.085908637307684 ]}
                        zoom={14}
                        withBounds={{ padding: [ 50, 50 ] }}
                        clusterize={true}>
            {data?.search.accommodationMapMarkers.map((marker, i) => <MapAsyncPopupMarker key={i} marker={marker} />)}
          </MapContainer>
        </aside>
      </div>

    </Wrapper.Centered>);
};

export default SearchPage;
